// Admin analytics dashboard — funnel + revenue + top destinations + top suppliers.
import React, { useEffect, useState } from 'react';
import { TrendingUp, IndianRupee, Layers, Users } from 'lucide-react';
import { api } from '../../lib/api';
import { Card, CardContent } from '../ui/card';

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <Card><CardContent className="p-4 sm:p-5">
    <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <p className="text-2xl sm:text-3xl font-bold mt-1 text-gray-900 dark:text-white">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </CardContent></Card>
);

const FunnelBar = ({ label, value, max }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="py-2" data-testid={`funnel-${label}`}>
      <div className="flex items-baseline justify-between text-xs mb-1">
        <span className="font-semibold text-gray-700 dark:text-gray-200 capitalize">{label.replace(/_/g, ' ')}</span>
        <span className="font-mono text-gray-600 dark:text-gray-300">{value}</span>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-red-500 to-red-700" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/analytics/dashboard')
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-center text-gray-500">Loading analytics…</div>;
  if (!data) return <div className="p-10 text-center text-red-600">Could not load analytics.</div>;

  const funnel = data.funnel || {};
  const max = Math.max(1, ...Object.values(funnel));
  const fmtINR = (n) => '₹' + Math.round(n || 0).toLocaleString('en-IN');

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Analytics</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={IndianRupee} label="Revenue (lifetime)" value={fmtINR(data.revenue)} />
        <StatCard icon={Layers}       label="Total bookings"    value={data.total_bookings} />
        <StatCard icon={TrendingUp}   label="Conversion rate"   value={`${data.conversion_rate}%`} sub="confirmed / searched" />
        <StatCard icon={Users}        label="Funnel events"     value={Object.values(funnel).reduce((a, b) => a + b, 0)} sub="last 30 days" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2"><CardContent className="p-5">
          <h2 className="font-bold text-gray-900 dark:text-white mb-2">Booking Funnel</h2>
          <p className="text-xs text-gray-500 mb-3">From homepage view through to booking confirmation.</p>
          {Object.entries(funnel).map(([k, v]) => <FunnelBar key={k} label={k} value={v} max={max} />)}
        </CardContent></Card>

        <div className="space-y-4">
          <Card><CardContent className="p-5">
            <h2 className="font-bold text-gray-900 dark:text-white mb-3">Top destinations</h2>
            {data.top_destinations.length === 0
              ? <p className="text-sm text-gray-500">No data yet.</p>
              : <ul className="space-y-2 text-sm" data-testid="top-destinations">
                  {data.top_destinations.map((d, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      <span className="font-medium text-gray-800 dark:text-gray-200 truncate">{d.city}</span>
                      <span className="text-gray-500 whitespace-nowrap">{d.bookings} · {fmtINR(d.revenue)}</span>
                    </li>
                  ))}
                </ul>}
          </CardContent></Card>
          <Card><CardContent className="p-5">
            <h2 className="font-bold text-gray-900 dark:text-white mb-3">Top suppliers</h2>
            {data.top_suppliers.length === 0
              ? <p className="text-sm text-gray-500">No data yet.</p>
              : <ul className="space-y-2 text-sm" data-testid="top-suppliers">
                  {data.top_suppliers.map((s, i) => (
                    <li key={i} className="flex justify-between"><span>{s.supplier}</span><span>{s.bookings}</span></li>
                  ))}
                </ul>}
          </CardContent></Card>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
